import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Check, X, Search, Plus, Trash2, Eye, EyeOff, Bot, Loader2, Image as ImageIcon, PartyPopper, Award } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardEvents() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('eventi'); // 'eventi' | 'riconoscimenti' | 'manage' | 'manual'
  const [igPosts, setIgPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventi, setSelectedEventi] = useState(new Set());
  const [selectedRicon, setSelectedRicon] = useState(new Set());
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  // Manual form
  const [manualTitle, setManualTitle] = useState('');
  const [manualCaption, setManualCaption] = useState('');
  const [manualImage, setManualImage] = useState('');
  const [manualLink, setManualLink] = useState('');
  const [manualCategory, setManualCategory] = useState('eventi');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [igRes, evRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/events/instagram-all`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/events/all`, { headers }).catch(() => ({ data: [] })),
      ]);
      setIgPosts(igRes.data);
      setEvents(evRes.data);
      const selE = new Set();
      const selR = new Set();
      igRes.data.forEach(p => {
        if (p.selected_category === 'eventi') selE.add(p.id);
        if (p.selected_category === 'riconoscimenti') selR.add(p.id);
      });
      setSelectedEventi(selE);
      setSelectedRicon(selR);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id, category) => {
    if (category === 'eventi') {
      setSelectedEventi(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    } else {
      setSelectedRicon(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
      });
    }
  };

  const saveSelection = async (category) => {
    setSaving(true);
    const ids = category === 'eventi' ? [...selectedEventi] : [...selectedRicon];
    try {
      const res = await axios.post(`${BACKEND_URL}/api/events/select`, { instagram_ids: ids, category }, { headers });
      toast.success(res.data.message);
      loadData();
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const togglePublished = async (id) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/events/${id}/toggle`, {}, { headers });
      toast.success(res.data.published ? 'Pubblicato' : 'Nascosto');
      loadData();
    } catch { toast.error('Errore'); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Eliminare questo evento?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/events/${id}`, { headers });
      toast.success('Evento eliminato');
      loadData();
    } catch { toast.error('Errore'); }
  };

  const addManual = async () => {
    if (!manualTitle) { toast.error('Inserisci un titolo'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/events/manual`, {
        title: manualTitle, caption: manualCaption, image_url: manualImage, permalink: manualLink, category: manualCategory
      }, { headers });
      toast.success('Evento aggiunto');
      setManualTitle(''); setManualCaption(''); setManualImage(''); setManualLink('');
      loadData();
    } catch { toast.error('Errore'); }
  };

  const askAI = async () => {
    setAiLoading(true);
    setAiSuggestion('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/events/ai-suggest`, {}, { headers });
      setAiSuggestion(res.data.suggestions_text || 'Nessun suggerimento disponibile.');
    } catch { toast.error('Errore AI'); }
    finally { setAiLoading(false); }
  };

  const filteredPosts = igPosts.filter(p => {
    if (!filter) return true;
    return (p.caption || '').toLowerCase().includes(filter.toLowerCase());
  });

  const currentSelected = tab === 'eventi' ? selectedEventi : selectedRicon;

  const tabs = [
    { key: 'eventi', label: 'Eventi', icon: PartyPopper, color: 'from-[#0066b2] to-[#005494]' },
    { key: 'riconoscimenti', label: 'Riconoscimenti', icon: Award, color: 'from-amber-500 to-amber-600' },
    { key: 'manage', label: 'Gestisci Tutti', icon: Eye, color: '' },
    { key: 'manual', label: 'Aggiungi Manuale', icon: Plus, color: '' },
  ];

  const isSelectTab = tab === 'eventi' || tab === 'riconoscimenti';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="dashboard-events-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                Eventi e Riconoscimenti
              </h1>
              <p className="text-gray-600">Seleziona i post da Instagram per ciascuna sezione</p>
            </div>
            <button
              onClick={askAI}
              disabled={aiLoading}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:opacity-90 transition-all shadow-lg"
              data-testid="ai-suggest-btn"
            >
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
              Suggerisci con AI
            </button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  tab === t.key
                    ? t.color ? `bg-gradient-to-r ${t.color} text-white shadow-lg` : 'bg-gray-900 text-white shadow-lg'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`tab-${t.key}`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>

          {/* AI Suggestion */}
          {aiSuggestion && (
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-2xl p-6 mb-8" data-testid="ai-suggestion">
              <div className="flex items-center gap-2 mb-3">
                <Bot className="w-5 h-5 text-cyan-600" />
                <h3 className="font-bold text-gray-900">Suggerimenti AI</h3>
                <button onClick={() => setAiSuggestion('')} className="ml-auto text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
              </div>
              <p className="text-gray-700 text-sm whitespace-pre-wrap">{aiSuggestion}</p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* TAB: Select from Instagram (Eventi or Riconoscimenti) */}
              {isSelectTab && (
                <div>
                  <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      {tab === 'eventi'
                        ? <PartyPopper className="w-5 h-5 text-[#0066b2]" />
                        : <Award className="w-5 h-5 text-amber-600" />}
                      <h2 className="text-lg font-bold text-gray-900">
                        Seleziona post per: {tab === 'eventi' ? 'Eventi' : 'Riconoscimenti'}
                      </h2>
                    </div>
                    <p className="text-gray-500 text-sm mb-4">
                      Clicca sui post che vuoi mostrare nella sezione "{tab === 'eventi' ? 'Eventi' : 'Riconoscimenti'}" della pagina pubblica.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Filtra per parola chiave o hashtag..."
                          value={filter}
                          onChange={e => setFilter(e.target.value)}
                          className="pl-10"
                          data-testid="filter-input"
                        />
                      </div>
                      <Button
                        onClick={() => saveSelection(tab)}
                        disabled={saving}
                        className={tab === 'eventi' ? 'bg-[#0066b2] hover:bg-[#005494]' : 'bg-amber-500 hover:bg-amber-600'}
                        data-testid="save-selection-btn"
                      >
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                        Salva Selezione ({currentSelected.size})
                      </Button>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 mb-4">{filteredPosts.length} post Instagram trovati (dal primo all'ultimo)</p>

                  {filteredPosts.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Instagram className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Nessun post Instagram trovato.</p>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredPosts.map((post, idx) => {
                        const isSelected = currentSelected.has(post.id);
                        const otherCat = tab === 'eventi' ? 'riconoscimenti' : 'eventi';
                        const isInOther = post.selected_category === otherCat;
                        return (
                          <div
                            key={post.id}
                            onClick={() => !isInOther && toggleSelect(post.id, tab)}
                            className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                              isInOther
                                ? 'border-gray-200 opacity-40 cursor-not-allowed'
                                : isSelected
                                  ? tab === 'eventi'
                                    ? 'border-[#0066b2] ring-2 ring-[#0066b2]/30 shadow-lg cursor-pointer'
                                    : 'border-amber-500 ring-2 ring-amber-500/30 shadow-lg cursor-pointer'
                                  : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                            }`}
                            data-testid={`ig-post-${idx}`}
                          >
                            <img
                              src={post.thumbnail_url || post.media_url}
                              alt=""
                              className="w-full h-48 object-cover"
                              referrerPolicy="no-referrer"
                            />
                            {isSelected && (
                              <div className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center shadow ${
                                tab === 'eventi' ? 'bg-[#0066b2]' : 'bg-amber-500'
                              }`}>
                                <Check className="w-4 h-4 text-white" />
                              </div>
                            )}
                            {isInOther && (
                              <div className="absolute top-2 left-2 bg-gray-800 text-white text-[10px] px-2 py-0.5 rounded-full">
                                In {otherCat === 'eventi' ? 'Eventi' : 'Riconoscimenti'}
                              </div>
                            )}
                            <div className="p-3">
                              <p className="text-xs text-gray-600 line-clamp-3">{post.caption}</p>
                              <p className="text-[10px] text-gray-400 mt-1">
                                {post.timestamp ? new Date(post.timestamp).toLocaleDateString('it-IT') : ''}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* TAB: Manage */}
              {tab === 'manage' && (
                <div>
                  {events.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <p>Nessun evento ancora. Seleziona post nelle tab "Eventi" o "Riconoscimenti".</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {events.map((event, idx) => (
                        <div
                          key={event.id}
                          className="flex items-center gap-4 bg-white rounded-xl border border-gray-200 p-4"
                          data-testid={`manage-event-${idx}`}
                        >
                          {event.image_url ? (
                            <img src={event.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" referrerPolicy="no-referrer" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                              <ImageIcon className="w-6 h-6 text-gray-300" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{event.title || (event.caption?.substring(0, 80) + '...')}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                event.category === 'eventi' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {event.category === 'eventi' ? 'Evento' : 'Riconoscimento'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                event.source === 'instagram' ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-500'
                              }`}>
                                {event.source === 'instagram' ? 'Instagram' : 'Manuale'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                event.published ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'
                              }`}>
                                {event.published ? 'Pubblicato' : 'Nascosto'}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button variant="outline" size="sm" onClick={() => togglePublished(event.id)} data-testid={`toggle-event-${idx}`}>
                              {event.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 hover:border-red-300"
                              onClick={() => deleteEvent(event.id)} data-testid={`delete-event-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TAB: Manual */}
              {tab === 'manual' && (
                <div className="max-w-2xl">
                  <div className="bg-white rounded-2xl border border-gray-200 p-8 space-y-5">
                    <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
                      Aggiungi Evento/Riconoscimento Manuale
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setManualCategory('eventi')}
                          className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                            manualCategory === 'eventi' ? 'bg-[#0066b2] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          data-testid="manual-cat-eventi"
                        >
                          <PartyPopper className="w-4 h-4" /> Evento
                        </button>
                        <button
                          onClick={() => setManualCategory('riconoscimenti')}
                          className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                            manualCategory === 'riconoscimenti' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          data-testid="manual-cat-riconoscimenti"
                        >
                          <Award className="w-4 h-4" /> Riconoscimento
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titolo *</label>
                      <Input value={manualTitle} onChange={e => setManualTitle(e.target.value)} placeholder="Es: Certificazione Bosch 2026" data-testid="manual-title" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                      <textarea
                        value={manualCaption}
                        onChange={e => setManualCaption(e.target.value)}
                        placeholder="Descrizione dell'evento..."
                        className="w-full rounded-lg border border-gray-200 p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-[#0066b2]"
                        data-testid="manual-caption"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">URL Immagine</label>
                      <Input value={manualImage} onChange={e => setManualImage(e.target.value)} placeholder="https://..." data-testid="manual-image" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Link Esterno</label>
                      <Input value={manualLink} onChange={e => setManualLink(e.target.value)} placeholder="https://www.instagram.com/p/..." data-testid="manual-link" />
                    </div>
                    <Button onClick={addManual} className={manualCategory === 'eventi' ? 'bg-[#0066b2] hover:bg-[#005494] w-full' : 'bg-amber-500 hover:bg-amber-600 w-full'} data-testid="add-manual-btn">
                      <Plus className="w-4 h-4 mr-2" /> Aggiungi {manualCategory === 'eventi' ? 'Evento' : 'Riconoscimento'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
